import { Box, DialogActions, DialogContent, styled } from '@mui/material';
import WarningBanner from 'components/DataImport/WarningBanner';
import { FlexBox } from 'components/Structure';
import { DialogDefault, ThemeButton, ThemeTypography } from 'designSystem';
import GeoDataItem from 'designSystem/DataDisplay/GeoDataItem/GeoDataItem';
import Icon from 'designSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import Moment from 'react-moment';
import { booleanish } from 'types/booleanish.types';
import { ERiskLevel } from 'types/compliance.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { EGeoDataEntity, IGeoDataProcess } from 'types/geodata.types';
import { useMutation } from '@apollo/client';
import { EXPORT_GEO_DATA_PROCESS_REPORT } from 'graphql/secure/mutations/dueDiligenceProcess.mutations';

interface IDownloadReportDialogProps extends IDefaultDialogProps {
  geoDataProcess: IGeoDataProcess;
}

const DueDiligenceContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: theme.custom.themeColors.grayScale[5],
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
  borderRadius: 8,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const DetailContainer = styled(Box)<{ fullWidth?: booleanish }>(({ theme, fullWidth }) => ({
  padding: theme.spacing(1),
  background: '#fff',
  borderRadius: 6,

  ...(fullWidth === 'true' && {
    width: '100%',
  }),
}));

const ButtonGroup = styled(DialogActions)(({ theme }) => ({
  boxShadow: theme.custom.shadows[1],
}));

const DownloadReportDialog: FC<IDownloadReportDialogProps> = ({
  geoDataProcess,
  open,
  onClose,
}) => {
  const [exportGeoDataProcessReport, { loading }] = useMutation(EXPORT_GEO_DATA_PROCESS_REPORT);

  const uniqueRawMaterials = Array.from(
    new Set(geoDataProcess?.geoDatas?.map(geoData => geoData.rawMaterial?.id))
  );

  const uniqueCountries = Array.from(
    new Set(geoDataProcess?.geoDatas?.map(geoData => geoData.originCountry))
  );

  const amountPlots = geoDataProcess?.geoDatas?.reduce(
    (acc, geoData) =>
      acc + (geoData.totalOriginalSiteCount - (geoData.excludedSitesIds?.length ?? 0)),
    0
  );

  const amountBatches = geoDataProcess?.geoDatas?.reduce(
    (acc, geoData) => acc + (geoData.geocBatches?.length ?? 0),
    0
  );

  const uniqueSuppliers = Array.from(
    new Set(geoDataProcess?.geoDatas?.map(geoData => geoData.ownedBy?.id))
  );

  const weightSum = geoDataProcess?.geoDatas?.reduce(
    (acc, geoData) =>
      acc + (geoData.geocBatches?.reduce((batchAcc, batch) => batchAcc + batch.weight, 0) ?? 0),
    0
  );

  const latestCheckData = geoDataProcess?.geoDatas?.reduce((latest, geoData) => {
    const checkDate = new Date(geoData.geocBatches?.[0]?.checkDate || 0);
    return checkDate > latest ? checkDate : latest;
  }, new Date(0));

  const earliestCheckData = geoDataProcess?.geoDatas?.reduce((earliest, geoData) => {
    const checkDate = new Date(geoData.geocBatches?.[0]?.checkDate || 0);
    return checkDate < earliest ? checkDate : earliest;
  }, new Date());

  const hasMediumOrHighRisk = geoDataProcess?.geoDatas?.some(
    geoData => geoData.riskStatus === ERiskLevel.HIGH || geoData.riskStatus === ERiskLevel.MEDIUM
  );

  const handleDownloadReport = async () => {
    const { data } = await exportGeoDataProcessReport({
      variables: {
        id: geoDataProcess.id,
      },
    });

    if (data?.exportGeoDataProcessReport?.url) {
      window.open(data.exportGeoDataProcessReport.url, '_blank');
    }
  };

  return (
    <DialogDefault
      open={open}
      title="Download report"
      data-cy="download-report-dialog"
      fullWidth
      iconName="download"
      maxWidth="xl"
      onClose={onClose}
    >
      <Box maxHeight={500} overflow="auto">
        <DialogContent>
          <ThemeTypography variant="HEADER_5">Due diligence Details</ThemeTypography>
          <DueDiligenceContainer>
            <FlexBox gap={1}>
              <DetailContainer>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">
                  {uniqueRawMaterials.length} commodit
                  {uniqueRawMaterials.length === 1 ? 'y' : 'ies'}
                </ThemeTypography>
              </DetailContainer>
              <DetailContainer>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">
                  {uniqueCountries.length} countr{uniqueCountries.length === 1 ? 'y' : 'ies'}
                </ThemeTypography>
              </DetailContainer>
              <DetailContainer>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">
                  {amountPlots} plot{amountPlots !== 1 && 's'}
                </ThemeTypography>
              </DetailContainer>
              <DetailContainer>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">
                  {amountBatches} batch{amountBatches !== 1 && 'es'}
                </ThemeTypography>
              </DetailContainer>
              <DetailContainer>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">{weightSum} kg</ThemeTypography>
              </DetailContainer>
              <DetailContainer>
                <ThemeTypography variant="BODY_MEDIUM_BOLD">
                  {uniqueSuppliers.length} supplier{uniqueSuppliers.length !== 1 ? 's' : ''}
                </ThemeTypography>
              </DetailContainer>
            </FlexBox>
            <DetailContainer fullWidth="true" mt={1}>
              <ThemeTypography variant="BODY_MEDIUM">
                The due diligence results of this report applies to the year of{' '}
                <b>
                  <Moment format="YYYY">{earliestCheckData}</Moment>
                </b>
                .
              </ThemeTypography>
            </DetailContainer>
            <DetailContainer fullWidth="true" mt={1}>
              <ThemeTypography variant="BODY_MEDIUM">
                The deforestation analysis of all origin plots applies to the period of{' '}
                <b>
                  <Moment format="YYYY-MM-DD">{earliestCheckData}</Moment>
                </b>{' '}
                to
                <b>
                  {' '}
                  <Moment format="YYYY-MM-DD">{latestCheckData}</Moment>
                </b>
                .
              </ThemeTypography>
            </DetailContainer>
          </DueDiligenceContainer>
          <ThemeTypography variant="HEADER_5">Included origin data</ThemeTypography>
          <Box mb={1} />
          {!!hasMediumOrHighRisk && (
            <Box mb={1.5}>
              <WarningBanner
                message="The results include high or medium risks. You can still export a report including risks but make sure you include more information to be compliant."
                badgeContent="Risk present"
                severity="warning"
              />
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap={1}>
            {geoDataProcess?.geoDatas?.map(geoData => (
              <GeoDataItem
                key={geoData.id}
                title={geoData.title}
                commodity={geoData.rawMaterial?.title}
                geoDataEntity={EGeoDataEntity[geoData.dataType]}
                disabled
                riskLevel={geoData.riskStatus ? ERiskLevel[geoData.riskStatus] : undefined}
                amountFarms={
                  geoData.totalOriginalSiteCount - (geoData.excludedSitesIds?.length ?? 0)
                }
                countryCode={geoData.originCountry}
                owner={geoData.ownedBy?.name}
                fullWidth
                hasBoxShadow
                amountBatches={geoData.geocBatches?.length}
                weight={geoData.geocBatches?.reduce((acc, batch) => acc + batch.weight, 0)}
              />
            ))}
          </Box>
        </DialogContent>
      </Box>
      <ButtonGroup>
        <Box display="flex" justifyContent="space-between" width="100%">
          <ThemeButton color="BLUE_ICE" onClick={onClose}>
            Cancel
          </ThemeButton>
          <ThemeButton
            startIcon={<Icon name="download" />}
            loading={loading}
            onClick={handleDownloadReport}
          >
            Download
          </ThemeButton>
        </Box>
      </ButtonGroup>
    </DialogDefault>
  );
};

export default DownloadReportDialog;
